import type { getHomeRecsQuery, getYourTeamBCQuery } from '../../__generated__/graphql';
import { YOUR_TEAM_CAROUSEL_NAME } from '../../constants/carousel';
import type { HomeRecsCollection } from '../../fixtures/collections';
import { isTruthy } from '../../utils/typescript';
import { usePrepareYourTeamData } from './usePrepareYourTeamData';

interface OrganizedCollections {
    firstCollection: HomeRecsCollection | null;
    secondaryCollections: HomeRecsCollection[];
    yourAgentCollection: HomeRecsCollection | null;
}

export const hasYourAgentCollection = (homeRecsQueryData?: getHomeRecsQuery) =>
    (homeRecsQueryData?.collections?.collections ?? []).some(
        (collection) => collection?.name === YOUR_TEAM_CAROUSEL_NAME
    );

/**
 * Prepares and returns the first, secondary and (if any) your agent collection
 * Based on the collections response from the HomeRecs query
 */
export const usePrepareCollections = ({
    homeRecsData,
    yourTeamData,
}: {
    homeRecsData?: getHomeRecsQuery;
    yourTeamData?: getYourTeamBCQuery;
}): OrganizedCollections => {
    const { paAgent } = usePrepareYourTeamData(yourTeamData);

    if (!homeRecsData?.collections?.collections) {
        return {
            firstCollection: null,
            secondaryCollections: [],
            yourAgentCollection: null,
        };
    }

    const collections = homeRecsData.collections.collections.filter(isTruthy);
    const yourAgentCollectionIdx = collections.findIndex(
        (collection) => collection.name === YOUR_TEAM_CAROUSEL_NAME
    );

    // Avoid mutating original array when using `splice`
    const collectionsWithoutYourAgent = [...collections];
    const [yourAgentCollection = null] =
        yourAgentCollectionIdx !== -1
            ? collectionsWithoutYourAgent.splice(yourAgentCollectionIdx, 1)
            : [];

    /**
     * Combine first collection with remaining collections when:
     *  - ("your agent" collection AND paAgent) exists
     */
    if (yourAgentCollection && paAgent) {
        return {
            firstCollection: null,
            secondaryCollections: collectionsWithoutYourAgent,
            yourAgentCollection,
        };
    }

    const [firstCollection = null, ...secondaryCollections] = collectionsWithoutYourAgent;
    return {
        firstCollection,
        secondaryCollections,
        yourAgentCollection: null,
    };
};
